import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "call-records" }
const _hoisted_2 = { class: "stats-grid" }
const _hoisted_3 = { class: "stat-card" }
const _hoisted_4 = { class: "stat-icon" }
const _hoisted_5 = { class: "stat-content" }
const _hoisted_6 = { class: "stat-number" }
const _hoisted_7 = { class: "stat-card" }
const _hoisted_8 = { class: "stat-icon" }
const _hoisted_9 = { class: "stat-content" }
const _hoisted_10 = { class: "stat-number" }
const _hoisted_11 = { class: "stat-card" }
const _hoisted_12 = { class: "stat-icon" }
const _hoisted_13 = { class: "stat-content" }
const _hoisted_14 = { class: "stat-number" }
const _hoisted_15 = { class: "stat-card" }
const _hoisted_16 = { class: "stat-icon" }
const _hoisted_17 = { class: "stat-content" }
const _hoisted_18 = { class: "stat-number" }
const _hoisted_19 = { class: "search-section" }
const _hoisted_20 = { class: "search-form" }
const _hoisted_21 = { class: "button-group" }
const _hoisted_22 = { class: "main-content" }
const _hoisted_23 = { class: "records-section" }
const _hoisted_24 = { class: "action-buttons-center" }
const _hoisted_25 = { class: "table-footer" }
const _hoisted_26 = { class: "chart-section" }
const _hoisted_27 = { class: "chart-container" }
const _hoisted_28 = { class: "chart-actions" }
const _hoisted_29 = { class: "audio-player-container" }
const _hoisted_30 = { class: "audio-info" }
const _hoisted_31 = { class: "audio-controls" }
const _hoisted_32 = ["src"]
const _hoisted_33 = { class: "dialog-footer" }

import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  reactive,
  watch,
  nextTick,
} from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Phone,
  Check,
  Close,
  Timer,
  Search,
  Refresh,
  Download,
  ArrowLeft,
  User,
} from "@element-plus/icons-vue";
import { useStore } from "vuex";
import apiClient from "../services/apiClient";
import config from "@/config";
import { logger } from "../utils/logger";
import {
  DataService,
  UserServiceApi,
  TenantServiceApi,
} from "../services/dataService";
import { useRoute, useRouter } from "vue-router";
import {
  ROLE_OPERATOR,
  ROLE_ADMIN,
  ROLE_SUPER_ADMIN,
} from "../types/permission";
import { formatDateTime } from "../utils/dateTimeUtils";
import * as echarts from "echarts";


export default /*@__PURE__*/_defineComponent({
  __name: 'CallRecords',
  setup(__props) {

const store = useStore();
const hasPermission = (perm: string) => store.getters.hasPermission(perm);
const route = useRoute();
const router = useRouter();

// 响应式状态定义
const loading = ref(false); // 控制加载状态
const records = ref([]); // 存储从后端获取的通话记录
const totalRecords = ref(0); // 总记录数，用于分页
// 用户显示名映射（username -> display_name）
const userDisplayMap = ref<Record<string, string>>({});
const displayToUserMap = ref<Record<string, string>>({});
// 租户选项（仅 super_admin 使用）
const tenants = ref<{ id: string | number; name: string }[]>([]);

// 图表相关状态
const chartRef = ref<HTMLElement>();
const chartInstance = ref<echarts.ECharts>();
const chartLoading = ref(false);
const chartData = reactive<Array<{ date: string; duration: number }>>([]);
// 年月显示（置于标题旁）
const currentYM = ref<string>("");
// 表格高度：窗口高度减去顶部块与图表区域的估算高度
const recordsTableHeight = computed(() => {
  // 视口高度 - 顶部统计 - 筛选区(更紧凑) - 图表卡片(300) - 其他边距
  const h = window.innerHeight - 110 - 70 - 300 - 70;
  // 提高最小高度以容纳更多行
  return Math.max(360, h);
});
// 播放对话框与引用
const playDialogVisible = ref(false);
const audioPlayer = ref<HTMLAudioElement | null>(null);
const currentAudio = reactive<{ url: string | null; title: string | null }>({
  url: null,
  title: null,
});

// 搜索表单
const searchForm = reactive({
  deviceName: "",
  status: "",
  dateRange: [] as string[],
  startTime: "",
  endTime: "",
  contactName: "", // 新增处理人员筛选字段
  tenantId: "", // super_admin 的租户筛选
});

// 分页
const currentPage = ref(1);
const pageSize = ref(20);

// 顶部的统计数据
const statsData = reactive({
  totalCalls: 0,
  connectedCalls: 0,
  missedCalls: 0,
  totalDuration: "0h 0m",
});

// 图表描述文本
const chartDescription = computed(() => {
  const deviceText = searchForm.deviceName
    ? `${searchForm.deviceName} 每日通话时长`
    : "所有设备每日通话时长";

  return deviceText;
});

// 获取月度通话时间统计数据
const fetchMonthlyCallStats = async () => {
  chartLoading.value = true;
  try {
    const currentUser = store.getters.getUser;
    const userRole = currentUser?.role || "";
    const userId = currentUser?.id;

    const params = {
      deviceName: searchForm.deviceName || undefined,
      user_role: userRole,
      user_id: userId,
    };

    logger.info("获取月度通话时间统计数据:", params);

    const response = await DataService.getMonthlyCallStats(params);
    const responseData = (response as any).data || response;
    const { code, data, message } = responseData;

    if (code === 0 && data) {
      // 验证数据结构
      const dailyStats = data.dailyStats || [];
      if (Array.isArray(dailyStats)) {
        // 清理和验证数据
        const validStats = dailyStats.filter(
          (item: any) =>
            item &&
            typeof item.date === "string" &&
            typeof item.duration === "number" &&
            !isNaN(item.duration) &&
            item.duration >= 0
        );

        // 清空现有数据并添加新数据
        chartData.splice(0, chartData.length);
        chartData.push(...validStats);

        // 设置当前年月展示
        if (validStats.length > 0 && typeof validStats[0].date === "string") {
          currentYM.value = String(validStats[0].date).slice(0, 7);
        } else {
          const now = new Date();
          currentYM.value = `${now.getFullYear()}-${String(
            now.getMonth() + 1
          ).padStart(2, "0")}`;
        }

        logger.info("月度通话时间统计数据获取成功:", validStats);
        logger.info("原始数据:", dailyStats);
        logger.info("设备筛选:", searchForm.deviceName);
        updateChart();
      } else {
        logger.error("月度统计数据格式错误:", data);
        ElMessage.error("数据格式错误");
        chartData.splice(0, chartData.length);
        updateChart();
      }
    } else {
      logger.error("获取月度通话时间统计数据失败:", message);
      ElMessage.error(message || "获取月度统计数据失败");
      chartData.splice(0, chartData.length);
      updateChart();
    }
  } catch (error) {
    logger.error("获取月度通话时间统计数据时发生错误:", error);
    ElMessage.error("网络错误，无法加载月度统计数据");
    chartData.splice(0, chartData.length);
    updateChart();
  } finally {
    chartLoading.value = false;
  }
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance.value = echarts.init(chartRef.value);

    // 监听窗口大小变化
    window.addEventListener("resize", () => {
      chartInstance.value?.resize();
    });
  }
};

// 更新图表
const updateChart = () => {
  if (!chartInstance.value) return;

  try {
    // 数据验证和清理
    const validData = chartData.filter(
      (item) =>
        item &&
        typeof item.date === "string" &&
        typeof item.duration === "number" &&
        !isNaN(item.duration) &&
        item.duration >= 0
    );

    if (validData.length === 0) {
      // 如果没有有效数据，显示空状态
      const option = {
        title: {
          text: "本月每日累计通话时长",
          left: "center",
          textStyle: {
            fontSize: 16,
            fontWeight: "bold",
          },
        },
        graphic: {
          type: "text",
          left: "center",
          top: "middle",
          style: {
            text: "暂无数据",
            fontSize: 14,
            fill: "#999",
          },
        },
      };
      try {
        chartInstance.value.setOption(option, true);
      } catch (chartError) {
        logger.error("设置图表空状态时出错:", chartError);
      }
      return;
    }

    const dates = validData.map((item) => item.date);
    const durations = validData.map((item) => item.duration);

    // 验证数据完整性
    if (dates.length !== durations.length) {
      logger.error("图表数据不匹配，日期和时长数组长度不一致");
      return;
    }

    // 顶部副标题显示 年-月
    const ymFromData =
      validData && validData.length > 0 && typeof validData[0].date === "string"
        ? String(validData[0].date).slice(0, 7)
        : `${new Date().getFullYear()}-${String(
            new Date().getMonth() + 1
          ).padStart(2, "0")}`;

    const option = {
      title: {
        text: currentYM.value || "",
        left: "center",
        textStyle: {
          fontSize: 28, // 与顶部统计数字接近
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "item",
        axisPointer: { type: "shadow" },
        formatter: function (params: any) {
          if (!params) return "";
          const seconds = typeof params.value === "number" ? params.value : 0;
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          const remainingSeconds = seconds % 60;

          let timeText = "";
          if (hours > 0) timeText += `${hours}小时`;
          if (minutes > 0) timeText += `${minutes}分钟`;
          if (remainingSeconds > 0 || (hours === 0 && minutes === 0))
            timeText += `${remainingSeconds}秒`;
          return `累计通话时长: ${timeText} (${seconds}秒)`;
        },
      },
      xAxis: {
        type: "category",
        // 横轴仅显示日
        data: dates.map((d) =>
          typeof d === "string" && d.length >= 10 ? d.slice(8, 10) : d
        ),
        axisLabel: {
          rotate: 0,
          fontSize: 12,
          margin: 8,
        },
        name: "日",
        nameLocation: "end",
        nameTextStyle: { fontSize: 12, color: "#666" },
      },
      yAxis: {
        type: "value",
        name: "累计通话时长(秒)",
        axisLabel: {
          formatter: function (value: number) {
            if (typeof value !== "number" || isNaN(value)) return "0s";
            const hours = Math.floor(value / 3600);
            const minutes = Math.floor((value % 3600) / 60);
            if (hours > 0) {
              return `${hours}h${minutes}m`;
            } else if (minutes > 0) {
              return `${minutes}m`;
            } else {
              return `${value}s`;
            }
          },
        },
      },
      series: [
        {
          name: "通话时长",
          type: "bar",
          data: durations.map((duration, index) => ({
            value: duration,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#83bff6" },
                { offset: 0.5, color: "#188df0" },
                { offset: 1, color: "#188df0" },
              ]),
            },
          })),
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#2378f7" },
                { offset: 0.7, color: "#2378f7" },
                { offset: 1, color: "#2378f7" },
              ]),
            },
          },
        },
      ],
      grid: {
        left: "10%",
        right: "10%",
        bottom: "20%",
        top: "15%",
      },
    };

    // 使用try-catch包装setOption调用
    try {
      chartInstance.value.setOption(option, true);
    } catch (chartError) {
      logger.error("设置图表选项时出错:", chartError);
      // 尝试显示错误状态
      try {
        chartInstance.value.setOption(
          {
            title: {
              text: "图表加载失败",
              left: "center",
              textStyle: {
                fontSize: 16,
                fontWeight: "bold",
                color: "#ff4d4f",
              },
            },
            graphic: {
              type: "text",
              left: "center",
              top: "middle",
              style: {
                text: "图表渲染失败，请刷新页面重试",
                fontSize: 14,
                fill: "#ff4d4f",
              },
            },
          },
          true
        );
      } catch (fallbackError) {
        logger.error("设置错误状态图表时也失败:", fallbackError);
      }
    }
  } catch (error) {
    logger.error("更新图表时发生错误:", error);
    // 尝试显示错误状态
    try {
      if (chartInstance.value) {
        chartInstance.value.setOption(
          {
            title: {
              text: "图表加载失败",
              left: "center",
              textStyle: {
                fontSize: 16,
                fontWeight: "bold",
                color: "#ff4d4f",
              },
            },
            graphic: {
              type: "text",
              left: "center",
              top: "middle",
              style: {
                text: "图表渲染失败，请刷新页面重试",
                fontSize: 14,
                fill: "#ff4d4f",
              },
            },
          },
          true
        );
      }
    } catch (fallbackError) {
      logger.error("设置错误状态图表时也失败:", fallbackError);
    }
  }
};

// 从后端获取通话记录的核心函数
const fetchCallRecords = async () => {
  loading.value = true;
  try {
    // 获取当前用户信息
    const currentUser = store.getters.getUser;
    const userRole = currentUser?.role || "";
    const userId = currentUser?.id;

    logger.info("当前用户信息:", currentUser);
    logger.info("用户角色:", userRole);
    logger.info("用户ID:", userId);

    // 添加更详细的调试信息
    console.log("=== 调试信息 ===");
    console.log("store.getters.getUser:", store.getters.getUser);
    console.log("store.getters.getUserRole:", store.getters.getUserRole);
    console.log("currentUser?.role:", currentUser?.role);
    console.log("userRole:", userRole);
    console.log("==================");

    // 将显示名转换为用户名再进行过滤
    const contactParam =
      getUsernameByDisplayName(searchForm.contactName) ||
      searchForm.contactName ||
      undefined;
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value,
      deviceName: searchForm.deviceName || undefined,
      status: searchForm.status || undefined,
      startTime: searchForm.startTime || undefined,
      endTime: searchForm.endTime || undefined,
      device_id: device_id.value || undefined, // 添加设备ID过滤
      contact_name: contactParam, // 处理人员筛选（显示名→用户名）
      // 传递用户角色信息，由后端根据角色返回相应数据
      user_role: userRole,
      user_id: userId,
      tenant_id: isSuperAdmin.value
        ? searchForm.tenantId || undefined
        : undefined,
    };

    logger.info("获取通话记录，用户角色:", userRole, "用户ID:", userId);

    // 并行获取通话记录和统计数据
    const [recordsResponse, statsResponse] = await Promise.all([
      DataService.getCallRecords(params),
      DataService.getCallRecordsStats(params),
    ]);

    // 处理通话记录数据
    const responseData = (recordsResponse as any).data || recordsResponse;
    const { code, data, message } = responseData;

    if (code === 0) {
      // 后端返回的是items，不是records
      records.value = data.items || data.records || [];
      if (showContactName.value) {
        await ensureUserDisplayMap();
      }
      totalRecords.value = data.total;

      // 调试日志：打印第一条记录的结构
      if (records.value.length > 0) {
        console.log("第一条记录结构:", records.value[0]);
      }
    } else {
      ElMessage.error(message || "获取通话记录失败");
    }

    // 处理统计数据
    logger.info("原始统计数据响应:", statsResponse);

    // 检查响应结构
    if (statsResponse && typeof statsResponse === "object") {
      const statsResponseData = (statsResponse as any).data || statsResponse;
      const statsCode = statsResponseData.code;
      const statsResult = statsResponseData.data || statsResponseData;

      logger.info("统计数据响应数据:", statsResponseData);
      logger.info("统计数据代码:", statsCode);
      logger.info("统计数据结果:", statsResult);

      if (statsCode === 0 && statsResult) {
        // 更新统计数据
        Object.assign(statsData, statsResult);
        logger.info("统计数据更新成功:", statsResult);
        logger.info("更新后的statsData:", statsData);
      } else {
        logger.warn("获取统计数据失败，使用默认值");
        logger.warn("statsResponseData:", statsResponseData);
        logger.warn("statsCode:", statsCode);
        logger.warn("statsResult:", statsResult);
      }
    } else {
      logger.error("统计数据响应格式错误:", statsResponse);
    }
  } catch (error) {
    logger.error("获取通话记录时发生错误:", error);
    ElMessage.error("网络错误，无法加载通话记录");
  } finally {
    loading.value = false;
  }
};

// 加载用户名到显示名映射
const ensureUserDisplayMap = async () => {
  if (Object.keys(userDisplayMap.value).length > 0) return;
  try {
    const resp = await UserServiceApi.listUsers({});
    const items = (resp as any)?.data?.data?.items || [];
    const map: Record<string, string> = {};
    const rmap: Record<string, string> = {};
    for (const u of items) {
      const uname = (u as any)?.username;
      const dname = (u as any)?.display_name;
      if (uname) map[uname] = dname || uname;
      if (dname) rmap[dname] = uname;
    }
    userDisplayMap.value = map;
    displayToUserMap.value = rmap;
  } catch (e) {
    // 忽略
  }
};

const getDisplayName = (username: string) => {
  if (!username) return "—";
  const cu = store.getters.getUser;
  if (cu?.username === username && cu?.display_name) return cu.display_name;
  return userDisplayMap.value[username] || username;
};

// 根据显示名称反查用户名（用于筛选）
const getUsernameByDisplayName = (display: string) => {
  if (!display) return "";
  return displayToUserMap.value[display] || "";
};

// 加载租户列表（仅 super_admin）
const loadTenants = async () => {
  if (!isSuperAdmin.value) return;
  try {
    const resp = await TenantServiceApi.listTenants({ page_size: 100 });
    const payload = (resp as any)?.data?.data;
    const items = Array.isArray(payload?.items) ? payload.items : [];
    tenants.value = items.map((t: any) => ({ id: t.id, name: t.name }));
  } catch (e) {
    // 忽略加载失败
  }
};

// 下拉展开时按需加载
const onTenantDropdownVisible = (v: boolean) => {
  if (v && tenants.value.length === 0) {
    loadTenants();
  }
};

// 搜索按钮点击事件
const searchRecords = () => {
  currentPage.value = 1;
  fetchCallRecords();
  fetchMonthlyCallStats();
};

// 重置搜索条件
const resetSearch = () => {
  searchForm.deviceName = "";
  searchForm.status = "";
  searchForm.dateRange = [];
  searchForm.startTime = "";
  searchForm.endTime = "";
  searchForm.contactName = ""; // 重置处理人员筛选
  searchForm.tenantId = ""; // 重置租户筛选
  searchRecords(); // 重置后重新搜索
};

// 处理日期范围变化
const handleDateChange = (dates: string[] | null) => {
  if (dates && dates.length === 2) {
    searchForm.startTime = dates[0];
    searchForm.endTime = dates[1];
  } else {
    searchForm.startTime = "";
    searchForm.endTime = "";
  }
};

// 监听分页变化
watch([currentPage, pageSize], fetchCallRecords);

// 监听设备名称变化，更新图表
watch(
  () => searchForm.deviceName,
  () => {
    fetchMonthlyCallStats();
  }
);

// 角色就绪后自动加载租户
watch(
  () => isSuperAdmin.value,
  (v) => {
    if (v && tenants.value.length === 0) {
      loadTenants();
    }
  }
);

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载通话记录");
  fetchCallRecords();
  fetchMonthlyCallStats(); // 同时更新图表
};

// 组件挂载时，自动加载第一页数据
onMounted(async () => {
  // 初始化图表
  initChart();

  // 获取通话记录（用户信息已在NavView中加载）
  // 若从设备详情跳转携带了 device_id，则默认筛选本月
  if (device_id.value) {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    searchForm.dateRange = [
      start.toISOString().slice(0, 19).replace("T", " "),
      end.toISOString().slice(0, 19).replace("T", " "),
    ];
    searchForm.startTime = searchForm.dateRange[0];
    searchForm.endTime = searchForm.dateRange[1];
  }
  fetchCallRecords();

  // 获取月度统计数据
  fetchMonthlyCallStats();

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);

  // super_admin 预加载租户列表
  if (isSuperAdmin.value) {
    await loadTenants();
  }
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  // 销毁图表实例
  if (chartInstance.value) {
    chartInstance.value.dispose();
  }
});

// 获取统计标题
const getStatsTitle = () => {
  if (
    !searchForm.deviceName &&
    !searchForm.status &&
    !searchForm.dateRange.length
  ) {
    return "当前显示最近一个月的通话记录统计";
  } else {
    return "当前显示搜索结果的通话记录统计";
  }
};

// 获取状态类型
const getStatusType = (status: string) => {
  const typeMap: Record<string, "success" | "info" | "danger" | "warning"> = {
    active: "warning",
    ended: "success",
    cancelled: "info",
    system_timeout: "danger",
    admin_forced_end: "warning",
    failed: "danger",
    // 兼容旧值
    connected: "success",
    missed: "info",
    ongoing: "warning",
    unanswered: "info",
    no_answer: "info",
  };
  return typeMap[status] || "info";
};

// 获取状态文本
const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    active: "进行中",
    ended: "已完成",
    cancelled: "取消",
    system_timeout: "系统超时",
    admin_forced_end: "管理员结束",
    failed: "失败",
    // 兼容旧值
    connected: "已完成",
    missed: "取消",
    ongoing: "进行中",
    unanswered: "取消",
    no_answer: "取消",
  };
  return textMap[status] || "未知";
};

// 查看记录详情
const viewRecord = async (record: any) => {
  console.log("查看记录详情:", record); // 调试日志

  try {
    // 调用API获取详细的事件信息和日志
    const response = await DataService.getEventDetail(record.event_id);
    console.log("事件详情API响应:", response); // 调试日志

    if (!response || response.data?.code !== 0) {
      ElMessage.error("获取事件详情失败");
      return;
    }

    const eventDetail = response.data.data;
    const event = eventDetail.event;
    const logs = eventDetail.logs || [];

    // 构建详情信息
    const details = {
      deviceName: record.deviceName || record.device_id,
      deviceId: event.device_id,
      eventId: event.event_id,
      callTime: record.callTime || event.start_time,
      status: getStatusText(event.status),
      duration: formatDuration(event.start_time, event.end_time),
      contactName: event.contact_name,
      startTime: event.start_time,
      endTime: event.end_time,
      logs: logs,
    };

    console.log("详情信息:", details); // 调试日志

    // 构建日志HTML
    const logsHtml =
      logs.length > 0
        ? `
      <div style="margin-top: 15px;">
        <h4 style="margin-bottom: 10px; color: #409EFF;">操作日志</h4>
        <div style="max-height: 300px; overflow-y: auto; border: 1px solid #e4e7ed; border-radius: 4px; padding: 10px;">
          ${logs
            .map(
              (log: any) => `
            <div style="margin-bottom: 8px; padding: 8px; background: #f5f7fa; border-radius: 4px; border-left: 3px solid #409EFF;">
              <div style="font-weight: bold; color: #303133;">${
                log.action
              }</div>
              <div style="font-size: 12px; color: #909399; margin-top: 2px;">
                操作者: ${log.operator} | 时间: ${new Date(
                log.created_at
              ).toLocaleString()}
              </div>
              ${
                log.message
                  ? `<div style="font-size: 12px; color: #606266; margin-top: 2px;">${log.message}</div>`
                  : ""
              }
            </div>
          `
            )
            .join("")}
        </div>
      </div>
    `
        : "";

    // 显示详情对话框
    await ElMessageBox.confirm(
      `
      <div style="text-align: left;">
        <p><strong>设备名称:</strong> ${details.deviceName}</p>
        <p><strong>设备编号:</strong> ${details.deviceId}</p>
        <p><strong>事件ID:</strong> ${details.eventId}</p>
        <p><strong>通话时间:</strong> ${details.callTime}</p>
        <p><strong>通话状态:</strong> ${details.status}</p>
        <p><strong>通话时长:</strong> ${details.duration}</p>
        ${
          showContactName.value
            ? `<p><strong>处理人员:</strong> ${
                details.contactName || "未知"
              }</p>`
            : ""
        }
        <p><strong>开始时间:</strong> ${details.startTime}</p>
        <p><strong>结束时间:</strong> ${details.endTime}</p>
        ${logsHtml}
      </div>
      `,
      "通话记录详情",
      {
        dangerouslyUseHTMLString: true,
        confirmButtonText: "确定",
        cancelButtonText: "关闭",
        customClass: "call-record-details-dialog",
      }
    );
  } catch (error) {
    // 忽略取消操作的错误
    if (error !== "cancel" && error !== "close") {
      console.error("弹窗错误:", error);
      ElMessage.error("获取事件详情失败");
    }
  }
};

// 播放记录：调用录音直链并在对话框播放
const playRecord = async (record: any) => {
  try {
    if (record.status !== "connected" && record.status !== "ended") {
      ElMessage.warning("No playable content for this record");
      return;
    }

    let recordingId: string | undefined =
      record.recording_id || record.recordingId;
    if (!recordingId) {
      const baseUrl = `${config.apiBaseUrl}/recordings`;
      const client = (await import("@/services/apiClient")).default;
      const startDate = (record.start_time || "").slice(0, 10);
      const resp = await client.get(baseUrl, {
        params: {
          page: 1,
          size: 20,
          device_id: record.device_id || record.deviceId,
          start_date: startDate || undefined,
          end_date: startDate || undefined,
        },
      });
      const payload = resp?.data?.data || resp?.data || {};
      const list: any[] = Array.isArray(payload.recordings)
        ? payload.recordings
        : [];
      console.log("获取到的录音列表:", list); // 调试日志
      const callStart = record.start_time
        ? new Date(record.start_time).getTime()
        : NaN;
      let best: any | null = null;
      let bestDelta = Number.POSITIVE_INFINITY;
      for (const r of list) {
        if (r?.status !== "completed") continue;
        const st = r?.start_time ? new Date(r.start_time).getTime() : NaN;
        if (!isNaN(st) && !isNaN(callStart)) {
          const delta = Math.abs(st - callStart);
          if (delta < bestDelta) {
            bestDelta = delta;
            best = r;
          }
        }
      }
      if (best) recordingId = best.recording_id || best.id;
    }

    console.log("最终使用的录音ID:", recordingId); // 调试日志

    if (!recordingId) {
      ElMessage.error("Recording not found for this call");
      return;
    }

    const baseUrl2 = `${config.apiBaseUrl}/recordings`;
    const client2 = (await import("@/services/apiClient")).default;
    const resp2 = await client2.get(`${baseUrl2}/${recordingId}/download-url`);
    const url = resp2?.data?.data?.url || resp2?.data?.url;
    console.log("获取到的播放URL:", url); // 调试日志
    if (!url) {
      ElMessage.error("Failed to get playback url");
      return;
    }

    currentAudio.url = url;
    currentAudio.title = record.deviceName || record.device_id || recordingId;
    playDialogVisible.value = true;
    await nextTick();
    try {
      const audio = audioPlayer.value;
      if (audio) {
        audio.src = url;
        await audio.play();
      }
    } catch (e) {
      console.warn("Auto play failed; user interaction may be required.", e);
    }
  } catch (error) {
    console.error("播放失败:", error);
    ElMessage.error("Play failed");
  }
};

// 导出记录
const exportRecords = () => {
  ElMessage.success("通话记录导出功能");
};

// 显示设备本月统计
const showDeviceStats = async (record: any) => {
  try {
    const deviceName = record.deviceName || record.device_id;

    // 设置设备名称筛选
    searchForm.deviceName = deviceName;

    // 更新图表描述
    logger.info(`显示设备 ${deviceName} 的本月统计`);

    // 重新获取该设备的月度统计数据
    await fetchMonthlyCallStats();

    ElMessage.success(`已切换到设备 ${deviceName} 的本月统计`);
  } catch (error) {
    logger.error("显示设备统计失败:", error);
    ElMessage.error("获取设备统计失败");
  }
};

// 重置图表筛选条件
const resetChartFilter = async () => {
  try {
    // 清除设备筛选
    searchForm.deviceName = "";

    // 重新获取所有设备的月度统计数据
    await fetchMonthlyCallStats();

    ElMessage.success("已重置为所有设备的统计");
  } catch (error) {
    logger.error("重置图表筛选失败:", error);
    ElMessage.error("重置失败");
  }
};

// 返回设备管理页面
const goBack = () => {
  router.push({ name: "设备管理" });
};

// 获取页面标题
const pageTitle = computed(() => {
  if (route.params.device_id) {
    return `通话记录 - ${route.params.device_id}`;
  }
  return "通话记录";
});

// 获取设备ID
const device_id = computed(() => {
  return route.params.device_id as string;
});

// 获取当前用户权限范围提示
const permissionHint = computed(() => {
  const currentUser = store.getters.getUser;
  const userRole = currentUser?.role || "";

  switch (userRole) {
    case ROLE_OPERATOR:
      return "当前显示您处理过的通话记录";
    case ROLE_ADMIN:
      return "当前显示所有通话记录";
    case ROLE_SUPER_ADMIN:
      return "当前显示所有通话记录";
    default:
      return "当前显示通话记录";
  }
});

// 获取当前用户角色
const currentUserRole = computed(() => {
  const currentUser = store.getters.getUser;
  return currentUser?.role || "";
});

// 是否为 super_admin
const isSuperAdmin = computed(() => currentUserRole.value === ROLE_SUPER_ADMIN);

// 判断是否显示contact名字（admin和super_admin显示）
const showContactName = computed(() => {
  return (
    currentUserRole.value === ROLE_ADMIN ||
    currentUserRole.value === ROLE_SUPER_ADMIN
  );
});

// 判断是否可以筛选处理人员
const canFilterByContactName = computed(() => {
  return (
    currentUserRole.value === ROLE_ADMIN ||
    currentUserRole.value === ROLE_SUPER_ADMIN
  );
});

// 格式化通话时长
const formatDuration = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) {
    return "未知";
  }

  try {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();

    if (durationMs <= 0) {
      return "0秒";
    }

    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}小时${minutes % 60}分钟`;
    } else if (minutes > 0) {
      return `${minutes}分钟${seconds % 60}秒`;
    } else {
      return `${seconds}秒`;
    }
  } catch (error) {
    return "格式错误";
  }
};

// (其余静态方法暂时保留，但需要审阅)

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_el_icon, null, {
              default: _withCtx(() => [
                _createVNode(_unref(Phone))
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, _toDisplayString(statsData.totalCalls), 1),
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "stat-label" }, "总通话次数", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_component_el_icon, null, {
              default: _withCtx(() => [
                _createVNode(_unref(Check))
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, _toDisplayString(statsData.connectedCalls), 1),
            _cache[10] || (_cache[10] = _createElementVNode("div", { class: "stat-label" }, "接通次数", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_component_el_icon, null, {
              default: _withCtx(() => [
                _createVNode(_unref(Close))
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", _hoisted_14, _toDisplayString(statsData.missedCalls), 1),
            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "stat-label" }, "未接通", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_component_el_icon, null, {
              default: _withCtx(() => [
                _createVNode(_unref(Timer))
              ]),
              _: 1
            })
          ]),
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", _hoisted_18, _toDisplayString(statsData.totalDuration), 1),
            _cache[12] || (_cache[12] = _createElementVNode("div", { class: "stat-label" }, "总通话时长", -1))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(_component_el_card, { class: "search-card" }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_20, [
              _createVNode(_component_el_row, { gutter: 20 }, {
                default: _withCtx(() => [
                  (!device_id.value)
                    ? (_openBlock(), _createBlock(_component_el_col, {
                        key: 0,
                        span: 5
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: searchForm.deviceName,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchForm.deviceName) = $event)),
                            placeholder: "设备名称",
                            clearable: ""
                          }, {
                            prefix: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Search))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["modelValue"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_col, { span: 3 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_select, {
                        modelValue: searchForm.status,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchForm.status) = $event)),
                        placeholder: "通话状态",
                        clearable: ""
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_option, {
                            label: "全部",
                            value: ""
                          }),
                          _createVNode(_component_el_option, {
                            label: "进行中",
                            value: "active"
                          }),
                          _createVNode(_component_el_option, {
                            label: "已完成",
                            value: "ended"
                          }),
                          _createVNode(_component_el_option, {
                            label: "取消(未接通)",
                            value: "cancelled"
                          }),
                          _createVNode(_component_el_option, {
                            label: "系统超时",
                            value: "system_timeout"
                          }),
                          _createVNode(_component_el_option, {
                            label: "管理员结束",
                            value: "admin_forced_end"
                          }),
                          _createVNode(_component_el_option, {
                            label: "失败",
                            value: "failed"
                          })
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  (isSuperAdmin.value)
                    ? (_openBlock(), _createBlock(_component_el_col, {
                        key: 1,
                        span: 4
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_select, {
                            modelValue: searchForm.tenantId,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchForm.tenantId) = $event)),
                            placeholder: "租户",
                            clearable: "",
                            onVisibleChange: onTenantDropdownVisible
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tenants.value, (t) => {
                                return (_openBlock(), _createBlock(_component_el_option, {
                                  key: t.id,
                                  label: t.name,
                                  value: t.id
                                }, null, 8, ["label", "value"]))
                              }), 128))
                            ]),
                            _: 1
                          }, 8, ["modelValue"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (canFilterByContactName.value)
                    ? (_openBlock(), _createBlock(_component_el_col, {
                        key: 2,
                        span: 3
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_el_input, {
                            modelValue: searchForm.contactName,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchForm.contactName) = $event)),
                            placeholder: "处理人员",
                            clearable: ""
                          }, {
                            prefix: _withCtx(() => [
                              _createVNode(_component_el_icon, null, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(User))
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["modelValue"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_col, { span: 7 }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_date_picker, {
                        modelValue: searchForm.dateRange,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((searchForm.dateRange) = $event)),
                        type: "datetimerange",
                        "range-separator": "至",
                        "start-placeholder": "开始时间",
                        "end-placeholder": "结束时间",
                        format: "YYYY-MM-DD HH:mm:ss",
                        "value-format": "YYYY-MM-DD HH:mm:ss",
                        onChange: handleDateChange
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_col, { span: 6 }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_21, [
                        _createVNode(_component_el_button, {
                          type: "primary",
                          onClick: searchRecords
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Search))
                              ]),
                              _: 1
                            }),
                            _cache[13] || (_cache[13] = _createTextVNode(" 搜索 ", -1))
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_button, { onClick: resetSearch }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_icon, null, {
                              default: _withCtx(() => [
                                _createVNode(_unref(Refresh))
                              ]),
                              _: 1
                            }),
                            _cache[14] || (_cache[14] = _createTextVNode(" 重置 ", -1))
                          ]),
                          _: 1
                        })
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("div", _hoisted_23, [
          _createVNode(_component_el_card, { class: "records-card" }, {
            default: _withCtx(() => [
              _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                data: records.value,
                style: {"width":"100%"},
                height: recordsTableHeight.value
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_table_column, {
                    prop: "device_id",
                    label: "设备编号",
                    "min-width": "200",
                    "show-overflow-tooltip": "",
                    "class-name": 'nowrap-cell table-th-compact'
                  }),
                  (isSuperAdmin.value)
                    ? (_openBlock(), _createBlock(_component_el_table_column, {
                        key: 0,
                        prop: "tenant_name",
                        label: "租户名称",
                        "min-width": "160",
                        "class-name": 'table-th-compact'
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_table_column, {
                    label: "通话时间",
                    "min-width": "200",
                    "class-name": 'table-th-compact'
                  }, {
                    default: _withCtx((scope) => [
                      _createTextVNode(_toDisplayString(_unref(formatDateTime)(scope.row.start_time)), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table_column, {
                    prop: "status",
                    label: "通话状态",
                    "min-width": "120",
                    "class-name": 'table-th-compact'
                  }, {
                    default: _withCtx((scope) => [
                      _createVNode(_component_el_tag, {
                        type: getStatusType(scope.row.status)
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(getStatusText(scope.row.status)), 1)
                        ]),
                        _: 2
                      }, 1032, ["type"])
                    ]),
                    _: 1
                  }),
                  (showContactName.value)
                    ? (_openBlock(), _createBlock(_component_el_table_column, {
                        key: 1,
                        prop: "contact_name",
                        label: "处理人员",
                        "min-width": "150",
                        "class-name": 'table-th-compact'
                      }, {
                        default: _withCtx((scope) => [
                          _createTextVNode(_toDisplayString(getDisplayName(scope.row.contact_name)), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_el_table_column, {
                    label: "通话时长",
                    "min-width": "120",
                    "class-name": 'table-th-compact'
                  }, {
                    default: _withCtx((scope) => [
                      _createTextVNode(_toDisplayString(formatDuration(scope.row.start_time, scope.row.end_time)), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_table_column, {
                    label: "操作",
                    "min-width": "250",
                    align: "center"
                  }, {
                    default: _withCtx((scope) => [
                      _createElementVNode("div", _hoisted_24, [
                        (hasPermission('call.view'))
                          ? (_openBlock(), _createBlock(_component_el_button, {
                              key: 0,
                              type: "primary",
                              size: "small",
                              onClick: ($event: any) => (viewRecord(scope.row)),
                              style: {"padding":"4px 8px","font-size":"12px"}
                            }, {
                              default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                _createTextVNode(" 详情 ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"]))
                          : _createCommentVNode("", true),
                        (hasPermission('call.view'))
                          ? (_openBlock(), _createBlock(_component_el_button, {
                              key: 1,
                              type: "info",
                              size: "small",
                              onClick: ($event: any) => (playRecord(scope.row)),
                              style: {"padding":"4px 8px","font-size":"12px"}
                            }, {
                              default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                _createTextVNode(" 播放 ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"]))
                          : _createCommentVNode("", true),
                        (hasPermission('call.view'))
                          ? (_openBlock(), _createBlock(_component_el_button, {
                              key: 2,
                              type: "success",
                              size: "small",
                              onClick: ($event: any) => (showDeviceStats(scope.row)),
                              style: {"padding":"4px 8px","font-size":"12px"}
                            }, {
                              default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                _createTextVNode(" 本月统计 ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["onClick"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["data", "height"])), [
                [_directive_loading, loading.value]
              ]),
              _createElementVNode("div", _hoisted_25, [
                _createVNode(_component_el_pagination, {
                  "current-page": currentPage.value,
                  "onUpdate:currentPage": _cache[5] || (_cache[5] = ($event: any) => ((currentPage).value = $event)),
                  "page-size": pageSize.value,
                  "onUpdate:pageSize": _cache[6] || (_cache[6] = ($event: any) => ((pageSize).value = $event)),
                  "page-sizes": [10, 20, 50, 100],
                  total: totalRecords.value,
                  layout: "total, sizes, prev, pager, next, jumper"
                }, null, 8, ["current-page", "page-size", "total"])
              ])
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_26, [
          _createVNode(_component_el_card, { class: "chart-card" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("div", {
                  ref_key: "chartRef",
                  ref: chartRef,
                  class: "chart"
                }, null, 512),
                _createElementVNode("div", _hoisted_28, [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    size: "small",
                    onClick: exportRecords
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Download))
                        ]),
                        _: 1
                      }),
                      _cache[18] || (_cache[18] = _createTextVNode(" 导出记录 ", -1))
                    ]),
                    _: 1
                  })
                ])
              ])
            ]),
            _: 1
          })
        ])
      ])
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: playDialogVisible.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((playDialogVisible).value = $event)),
      title: "播放通话录音",
      width: "500px",
      "close-on-click-modal": false,
      "close-on-press-escape": true
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_33, [
          _createVNode(_component_el_button, {
            onClick: _cache[7] || (_cache[7] = ($event: any) => (playDialogVisible.value = false))
          }, {
            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
              _createTextVNode("关闭", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_29, [
          _createElementVNode("div", _hoisted_30, [
            _createElementVNode("h4", null, _toDisplayString(currentAudio.title), 1)
          ]),
          _createElementVNode("div", _hoisted_31, [
            _createElementVNode("audio", {
              ref_key: "audioPlayer",
              ref: audioPlayer,
              controls: "",
              preload: "metadata",
              style: {"width":"100%"}
            }, [
              _createElementVNode("source", {
                src: currentAudio.url || '',
                type: "audio/mpeg"
              }, null, 8, _hoisted_32),
              _cache[19] || (_cache[19] = _createTextVNode(" Your browser does not support the audio element. ", -1))
            ], 512)
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})